import { Css, Icon } from "@homebound/beam";
import { useRef, useState } from "react";
import { Maybe } from "src/generated/graphqlTypes";

const excelMimeTypes = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

export type FileFieldProps = {
  label?: string;
  errors?: string[] | undefined;
  setFile: (file: File) => void;
  file?: Maybe<File>;
  // List of accepted file types, or "excel" for the Excel spreadsheet preset
  allowedFileTypes?: Array<string> | "excel";
};

/**
 * Provides a simple file input field that allows you to drag and drop files. This follows the display style of csv uploader.
 */
export function FileField(props: FileFieldProps) {
  const { label = "Drag & drop files", errors, file, setFile, allowedFileTypes } = props;
  const ref = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState<string | undefined>(undefined);

  const resolvedTypes = allowedFileTypes === "excel" ? excelMimeTypes : allowedFileTypes;
  const unsupportedTypeMessage =
    allowedFileTypes === "excel"
      ? "This file isn't a supported Excel format. Please upload a .xlsx or .xls file — not a .csv, PDF, or other format — and try again."
      : `The file must be a
    ${resolvedTypes?.map((type) => type.toUpperCase()).join(", ")}. Please use a different file.`;

  function handleOnClick(e: any) {
    ref?.current?.click();
  }

  const handleDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleDragLeave = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  function handleOnDrop(event: React.DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    const [file] = Array.from(event.dataTransfer.files);
    if (file) {
      if (resolvedTypes && !resolvedTypes.includes(file.type)) {
        setInternalError(unsupportedTypeMessage);
        return;
      } else {
        setInternalError(undefined);
      }
      setFile(file);
    }
  }

  function handleOnChange(event: React.ChangeEvent<HTMLInputElement>) {
    const [file] = event.target.files || [];
    if (file) {
      if (resolvedTypes && !resolvedTypes.includes(file.type)) {
        setInternalError(unsupportedTypeMessage);
        return;
      } else {
        setInternalError(undefined);
      }
      setFile(file);
    }
  }

  const hasErrors = errors?.nonEmpty || !!internalError;

  return (
    <>
      <div>
        <input type="file" ref={ref} hidden onChange={handleOnChange} />
        <button
          type="button"
          onClick={handleOnClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleOnDrop}
          css={
            Css.hPx(75)
              .w100.mt3.br8.bcBlue700.ba.bsDashed.relative.add("overflow", "hidden")
              .df.aic.jcc.cursorPointer.add("transition", "all 0.2s")
              .md.blue700.if(hasErrors).red700.onHover.bgGray300.$
          }
        >
          <div aria-hidden css={Css.absolute.left0.right0.top0.bottom0.bgBlue50.$} />
          <span css={Css.relative.z1.df.aic.$}>
            <span css={Css.mr1.$}>
              <Icon icon={file ? "file" : "cloudUpload"} />
            </span>
            {file?.name || label}
          </span>
        </button>
        <div css={Css.m2.red700.$}>{internalError || errors?.map((e, i) => <li key={i}>{e}</li>)}</div>
      </div>
    </>
  );
}
